import { useState } from "react";
import { Home } from "lucide-react";
import { Card, Modal, Field, inputCls } from "./ui";
import { fmtMoney, fmtDate } from "./store";

const ESTADO_STYLE = {
  "Disponible": { chip: "bg-emerald-50 text-emerald-600 border-emerald-200", ring: "" },
  "Reservado": { chip: "bg-amber-50 text-amber-600 border-amber-300", ring: "border-amber-300 bg-amber-50/40" },
  "Vendido": { chip: "bg-red-50 text-red-600 border-red-200", ring: "border-red-200 bg-red-50/30" },
};

export const Apartments = ({ data, upsertApto }) => {
  const { apartments, leads } = data;
  const [filter, setFilter] = useState("Todos");
  const [editing, setEditing] = useState(null);

  const counts = {
    Disponible: apartments.filter((a) => a.estado === "Disponible").length,
    Reservado: apartments.filter((a) => a.estado === "Reservado").length,
    Vendido: apartments.filter((a) => a.estado === "Vendido").length,
  };
  const ingresos = apartments.filter((a) => a.estado === "Vendido").reduce((s, a) => s + Number(a.precio), 0);
  const leadName = (id) => leads.find((l) => l.id === id)?.nombre || "";
  const shown = apartments.filter((a) => filter === "Todos" || a.estado === filter);

  const saveApto = (e) => {
    e.preventDefault();
    upsertApto({ ...editing, precio: Number(editing.precio) || 0 });
    setEditing(null);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card className="p-5"><p data-testid="apto-count-disponibles" className="text-2xl font-bold text-emerald-600">{counts.Disponible}</p><p className="text-xs text-gray-500">Disponibles</p></Card>
        <Card className="border-amber-200 bg-amber-50/50 p-5"><p data-testid="apto-count-reservados" className="text-2xl font-bold text-amber-600">{counts.Reservado}</p><p className="text-xs text-amber-700">Reservados</p></Card>
        <Card className="p-5"><p data-testid="apto-count-vendidos" className="text-2xl font-bold text-red-500">{counts.Vendido}</p><p className="text-xs text-gray-500">Vendidos</p></Card>
        <Card className="p-5"><p data-testid="apto-ingresos" className="text-2xl font-bold text-gray-900">{fmtMoney(ingresos)}</p><p className="text-xs text-gray-500">Ingresos</p></Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {["Todos", "Disponible", "Reservado", "Vendido"].map((f) => (
          <button
            key={f}
            data-testid={`apto-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${filter === f ? "border-[#2d5a3d] bg-[#2d5a3d] text-white" : "border-gray-200 bg-white text-gray-600 hover:border-[#2d5a3d]/40"}`}
          >
            {f === "Todos" ? "Todos" : `${f}s`}
          </button>
        ))}
        <span className="ml-auto self-center text-xs text-gray-400">{counts.Disponible} disponibles · {counts.Reservado} reservados · {counts.Vendido} vendidos de 18</span>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        {shown.map((a) => (
          <button
            key={a.id}
            data-testid={`apto-card-${a.numero}`}
            onClick={() => setEditing({ ...a })}
            className={`rounded-2xl border bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${ESTADO_STYLE[a.estado]?.ring || "border-gray-100"}`}
          >
            <div className="flex items-center justify-between">
              <Home className="h-4 w-4 text-gray-300" />
              <span className="text-[11px] text-gray-400">#{a.numero}</span>
            </div>
            <p className="mt-2 text-lg font-bold text-gray-900">{fmtMoney(a.precio)}</p>
            <span className={`mt-2 inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${ESTADO_STYLE[a.estado]?.chip || ""}`}>{a.estado}</span>
            {a.compradorId && <p className="mt-1.5 truncate text-[11px] text-gray-400">{leadName(a.compradorId)}</p>}
          </button>
        ))}
      </div>

      {editing && (
        <Modal title={`Apartamento ${editing.numero}`} onClose={() => setEditing(null)}>
          <form onSubmit={saveApto} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Estado">
                <select data-testid="apto-field-estado" className={inputCls} value={editing.estado} onChange={(e) => setEditing({ ...editing, estado: e.target.value })}>
                  {["Disponible", "Reservado", "Vendido"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Precio (€)">
                <input data-testid="apto-field-precio" type="number" className={inputCls} value={editing.precio} onChange={(e) => setEditing({ ...editing, precio: e.target.value })} />
              </Field>
              <Field label="Comprador asignado">
                <select data-testid="apto-field-comprador" className={inputCls} value={editing.compradorId || ""} onChange={(e) => setEditing({ ...editing, compradorId: e.target.value || null })}>
                  <option value="">— Sin asignar —</option>
                  {leads.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                </select>
              </Field>
              <Field label="Fecha de reserva">
                <input data-testid="apto-field-reserva" type="date" className={inputCls} value={editing.fechaReserva} onChange={(e) => setEditing({ ...editing, fechaReserva: e.target.value })} />
              </Field>
              <Field label="Fecha de venta">
                <input data-testid="apto-field-venta" type="date" className={inputCls} value={editing.fechaVenta} onChange={(e) => setEditing({ ...editing, fechaVenta: e.target.value })} />
              </Field>
            </div>
            <Field label="Notas">
              <textarea data-testid="apto-field-notas" rows={3} className={inputCls} value={editing.notas} onChange={(e) => setEditing({ ...editing, notas: e.target.value })} />
            </Field>
            {editing.fechaReserva && <p className="text-xs text-gray-400">Reservado el {fmtDate(editing.fechaReserva)}{editing.fechaVenta && ` · Vendido el ${fmtDate(editing.fechaVenta)}`}</p>}
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button data-testid="apto-save-btn" type="submit" className="rounded-lg bg-[#2d5a3d] px-5 py-2 text-sm font-semibold text-white hover:bg-[#3a7a50]">Guardar</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};
